import React, { createContext, useCallback, useEffect, useState } from 'react';
import LoadingIndicator from '../../../components/LoadingIndicator';
import eventBus from '../../../services/globalEventBus';
import { User } from '../domain/User';
import { getSessionUser } from '../api/getSessionUser';
import { transformSessionUser } from '../utils/transformSessionUser';
import { loadUserProfile } from '../utils/loadUserProfile';
import { refreshSessionUser } from '../utils/refreshSessionUser';
import { clearSessionForImpersonation } from '../utils/clearSessionForImpersonation';
import { clearSessionState } from '../utils/logout';
import { hasStoredUserSession, persistStoredUser } from '../utils/userStorage';
import { getJwt } from '../../../services/jwt';
import { UserResponse } from '../type/userResponse';

const REFRESH_USER_EVENTS = [
  'OrgAccountRoleUpdated',
  'RemoveFromRoleSucceeded',
  'OrgAccountRoleUnassigned',
  'AddToRoleSucceeded',
  'OrgAccountRoleAssignedById',
  'OrgAccountRoleAssigned',
  'RoleAssignedToUsers',
  'OrgAccountGroupDeleted',
];

type SessionState =
  | { status: 'identifying' }
  | { status: 'signingIn' }
  | { status: 'authenticated'; user: User }
  | { status: 'unauthenticated' };

export type UserContextValue = {
  user?: User;
  isLoading: boolean;
  refreshUser: () => Promise<User | undefined>;
  updateUser: (updater: (prev: User) => User) => void;
};

export const UserContext = createContext<UserContextValue>({
  user: undefined,
  isLoading: false,
  refreshUser: () => Promise.resolve(undefined),
  updateUser: () => undefined,
});
UserContext.displayName = 'UserContext';

type UserProviderProps = {
  children: React.ReactNode;
};

type SetSession = React.Dispatch<React.SetStateAction<SessionState>>;

function hasAuthenticatedSession(): boolean {
  try {
    if (getJwt()) return true;
    return hasStoredUserSession();
  } catch {
    return false;
  }
}

function setUserFromLoginPayload(payload: User | UserResponse): User {
  const user =
    'logInEmail' in payload && payload.logInEmail ? (payload as User) : transformSessionUser(payload as UserResponse);

  persistStoredUser(user);
  return user;
}

function applyLoadedUser(setSession: SetSession, loadedUser: User): void {
  setSession({ status: 'authenticated', user: loadedUser });
  loadUserProfile(loadedUser);
}

function refreshUserState(setSession: SetSession): Promise<User | undefined> {
  return refreshSessionUser().then(sessionUser => {
    if (sessionUser) {
      applyLoadedUser(setSession, sessionUser);
    }
    return sessionUser;
  });
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [session, setSession] = useState<SessionState>(() =>
    hasAuthenticatedSession() ? { status: 'identifying' } : { status: 'unauthenticated' }
  );

  const user = session.status === 'authenticated' ? session.user : undefined;
  const isLoading = session.status === 'identifying' || session.status === 'signingIn';

  const refreshUser = useCallback(() => refreshUserState(setSession), []);

  const updateUser = useCallback((updater: (prev: User) => User) => updater, []);

  useEffect(() => {
    if (!hasAuthenticatedSession()) {
      setSession({ status: 'unauthenticated' });
      return;
    }

    let cancelled = false;
    setSession({ status: 'identifying' });

    getSessionUser()
      .then(sessionUser => {
        if (cancelled) {
          return;
        }
        if (sessionUser) {
          applyLoadedUser(setSession, sessionUser);
        } else {
          setSession({ status: 'unauthenticated' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSession({ status: 'unauthenticated' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleUserLoggedIn = (payload: unknown) => {
      const userPayload = payload as User | UserResponse;

      if (userPayload.impersonating) {
        clearSessionForImpersonation();
      }

      const loadedUser = setUserFromLoginPayload(userPayload);
      applyLoadedUser(setSession, loadedUser);
    };

    const handleNoUserFound = () => {
      clearSessionState();
      setSession({ status: 'unauthenticated' });
    };

    const handleSignInStarted = () => {
      setSession(current => (current.status === 'authenticated' ? current : { status: 'signingIn' }));
    };

    const handleSignInFailed = () => {
      setSession(current => (current.status === 'signingIn' ? { status: 'unauthenticated' } : current));
    };

    const unsubscribers = [
      eventBus.subscribe('UserLoggedIn', handleUserLoggedIn),
      eventBus.subscribe('NoUserFound', handleNoUserFound),
      eventBus.subscribe('SignInStarted', handleSignInStarted),
      eventBus.subscribe('SignInFailed', handleSignInFailed),
      ...REFRESH_USER_EVENTS.map(eventName => eventBus.subscribe(eventName, () => refreshUserState(setSession))),
    ];

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  if (session.status === 'identifying') {
    return <LoadingIndicator />;
  }

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        refreshUser,
        updateUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
